import React from 'react';
import styles from './Badge.module.css';

/**
 * Badge Component
 *
 * A reusable badge component for displaying status indicators
 * following the AB Civil design system.
 *
 * @param {Object} props
 * @param {string} props.variant - Badge style variant: 'success' | 'warning' | 'danger' | 'info' | 'neutral'
 * @param {string} props.color - Custom color override (hex code)
 * @param {React.ReactNode} props.icon - Icon element to display
 * @param {boolean} props.dot - Show dot indicator instead of full background
 * @param {string} props.size - Badge size: 'sm' | 'md' | 'lg'
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Badge content
 */
const Badge = ({
  variant = 'neutral',
  color = null,
  icon = null,
  dot = false,
  size = 'md',
  className = '',
  children,
  ...rest
}) => {
  const classNames = [
    styles.badge,
    styles[variant],
    styles[size],
    dot && styles.dot,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const customStyle = color
    ? {
        backgroundColor: `${color}33`, // 20% opacity
        color: color,
        borderColor: `${color}4D`, // 30% opacity
      }
    : {};

  return (
    <span className={classNames} style={customStyle} {...rest}>
      {dot && <span className={styles.dotIndicator} />}
      {icon && <span className={styles.icon}>{icon}</span>}
      {children && <span className={styles.content}>{children}</span>}
    </span>
  );
};

export default Badge;
